import {type ReactNode, useCallback, useEffect, useMemo, useRef} from 'react';

import {useEagGridStore} from '../store/useEagGridStore';
import {FOCUS_EAGRID_EVENT} from '../utils/focusGridPart';
import {GridKeyboardNavContext, type GridKeyboardNavContextValue} from './GridKeyboardNavContext';

type GridNavRegistry = {
  searchContainer: HTMLElement | null;
  rows: Map<number, HTMLTableRowElement>;
  actionCells: Map<number, HTMLElement>;
};

/** Focusable action buttons inside a row's action cell, in DOM order, excluding disabled ones. */
function getActionButtons(cell: HTMLElement | undefined): HTMLElement[] {
  if (!cell) {
    return [];
  }
  return Array.from(cell.querySelectorAll<HTMLElement>('button:not([disabled])'));
}

/** Data cells (td[data-column]) inside a row, in DOM order. */
function getFocusableCells(tr: HTMLTableRowElement): HTMLElement[] {
  return Array.from(tr.querySelectorAll<HTMLElement>('td[data-column]'));
}

/** Row index whose registered action cell contains the focus target, or null. */
function findActionRowIndex(registry: GridNavRegistry, target: Node): number | null {
  for (const [index, cell] of registry.actionCells) {
    if (cell.contains(target)) {
      return index;
    }
  }
  return null;
}

/** Row index whose registered <tr> is, or contains, the focus target, or null. */
function findRowIndex(registry: GridNavRegistry, target: Node): number | null {
  for (const [index, tr] of registry.rows) {
    if (tr === target || tr.contains(target)) {
      return index;
    }
  }
  return null;
}

/** Column index of the cell that is or contains the focus target, or null if not a data cell. */
function findColumnIndex(tr: HTMLTableRowElement, target: Node): number | null {
  const cells = getFocusableCells(tr);
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] === target || cells[i].contains(target)) {
      return i;
    }
  }
  return null;
}

export function GridKeyboardNavProvider(props: {children: ReactNode}) {
  const store = useEagGridStore();
  const registryRef = useRef<GridNavRegistry>({
    searchContainer: null,
    rows: new Map(),
    actionCells: new Map(),
  });

  const registerSearchContainer = useCallback((el: HTMLElement | null) => {
    registryRef.current.searchContainer = el;
  }, []);

  const registerRow = useCallback((rowIndex: number, el: HTMLTableRowElement | null) => {
    if (el) {
      registryRef.current.rows.set(rowIndex, el);
    } else {
      registryRef.current.rows.delete(rowIndex);
    }
  }, []);

  const registerActionCell = useCallback((rowIndex: number, el: HTMLElement | null) => {
    if (el) {
      registryRef.current.actionCells.set(rowIndex, el);
    } else {
      registryRef.current.actionCells.delete(rowIndex);
    }
  }, []);

  const handleKeyDown = useCallback(
    // eslint-disable-next-line complexity, max-statements -- Pre-existing violation
    (event: React.KeyboardEvent) => {
      const registry = registryRef.current;
      const focusSearch = () => {
        registry.searchContainer?.querySelector<HTMLElement>('input')?.focus();
      };
      const focusCellNode = (rowIndex: number, columnIndex: number) => {
        const tr = registry.rows.get(rowIndex);
        if (!tr) {
          return;
        }
        const cells = getFocusableCells(tr);
        const clamped = Math.min(columnIndex, cells.length - 1);
        cells[clamped]?.focus();
      };

      const target = event.target as Node;

      // Resolve the focus zone from the ACTUAL focused element rather than trusting the store's
      // focusZone alone — DOM focus can arrive via mouse click, focus restoration, or after a
      // blur cleared the store, leaving focusZone out of sync. Deciding from the DOM keeps the
      // Tab loop correct however focus got here. The store is then synced for the row highlight.
      if (registry.searchContainer?.contains(target)) {
        if (event.key === 'Tab' && !event.shiftKey) {
          if (store.getState().rows.length === 0) {
            return;
          }
          event.preventDefault();
          store.enterRows();
          focusCellNode(0, 0);
        }
        return;
      }

      const actionRowIndex = findActionRowIndex(registry, target);
      if (actionRowIndex !== null) {
        const buttons = getActionButtons(registry.actionCells.get(actionRowIndex));
        const activeIndex = Math.max(0, buttons.indexOf(document.activeElement as HTMLElement));
        store.focusActionsAt(actionRowIndex, activeIndex);

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          store.moveAction(1, buttons.length);
          buttons[store.getState().focusedActionIndex]?.focus();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          store.moveAction(-1, buttons.length);
          buttons[store.getState().focusedActionIndex]?.focus();
        } else if (event.key === 'Tab' && !event.shiftKey) {
          event.preventDefault();
          store.exitToSearch();
          focusSearch();
        } else if (event.key === 'Tab' && event.shiftKey) {
          event.preventDefault();
          store.focusCellAt(actionRowIndex, 0);
          focusCellNode(actionRowIndex, 0);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          store.exitToSearch();
          focusSearch();
        }
        return;
      }

      const rowIndex = findRowIndex(registry, target);
      if (rowIndex !== null) {
        const tr = registry.rows.get(rowIndex);
        const columnIndex = tr
          ? (findColumnIndex(tr, target) ?? store.getState().focusedColumnIndex ?? 0)
          : 0;
        store.focusCellAt(rowIndex, columnIndex);

        const rows = store.getState().rows;
        const cells = tr ? getFocusableCells(tr) : [];

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          store.moveRow(1, rows.length);
          const newRowIndex = store.getState().focusedRowIndex ?? 0;
          focusCellNode(newRowIndex, store.getState().focusedColumnIndex ?? 0);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          store.moveRow(-1, rows.length);
          const newRowIndex = store.getState().focusedRowIndex ?? 0;
          focusCellNode(newRowIndex, store.getState().focusedColumnIndex ?? 0);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          store.moveColumn(1, cells.length);
          focusCellNode(rowIndex, store.getState().focusedColumnIndex ?? 0);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          store.moveColumn(-1, cells.length);
          focusCellNode(rowIndex, store.getState().focusedColumnIndex ?? 0);
        } else if (event.key === 'Tab' && !event.shiftKey) {
          event.preventDefault();
          const buttons = getActionButtons(registry.actionCells.get(rowIndex));
          if (buttons.length === 0) {
            store.exitToSearch();
            focusSearch();
          } else {
            store.enterActions();
            buttons[0].focus();
          }
        } else if (event.key === 'Tab' && event.shiftKey) {
          event.preventDefault();
          store.exitToSearch();
          focusSearch();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          registry.rows.get(rowIndex)?.click();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          store.exitToSearch();
          focusSearch();
        }
      }
    },
    [store]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        store.clearKeyboardFocus();
      }
    },
    [store]
  );

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: DOM event subscription (window custom event) with cleanup
  useEffect(
    function subscribeToFocusRequests() {
      const listener = (e: Event) => {
        if (!(e instanceof CustomEvent)) {
          return;
        }
        const {gridCode, part} = e.detail;
        if (gridCode !== store.getState().gridCode) {
          return;
        }

        const registry = registryRef.current;
        if (part === 'smartSearch') {
          registry.searchContainer?.querySelector<HTMLElement>('input')?.focus();
        } else if (part === 'row') {
          store.enterRows();
          const tr = registry.rows.get(0);
          if (tr) {
            const cells = getFocusableCells(tr);
            cells[0]?.focus();
          }
        }
      };

      window.addEventListener(FOCUS_EAGRID_EVENT, listener);
      return () => window.removeEventListener(FOCUS_EAGRID_EVENT, listener);
    },
    [store]
  );

  const value = useMemo<GridKeyboardNavContextValue>(
    () => ({registerSearchContainer, registerRow, registerActionCell, handleKeyDown, handleBlur}),
    [registerSearchContainer, registerRow, registerActionCell, handleKeyDown, handleBlur]
  );

  return (
    <GridKeyboardNavContext.Provider value={value}>
      {props.children}
    </GridKeyboardNavContext.Provider>
  );
}
